/**
 * Cook Log Sync Middleware
 *
 * Publishes the cook journal to Firestore, one entry at a time, through the
 * slice's outbox (`cookLog.pending`): an operation is cleared only once
 * Firestore acknowledges it, and whatever is left is retried on the next boot,
 * on reconnect and when the tab comes back to the foreground — the same three
 * passes as the ratings outbox (`flushPendingRecipeMetadataSync`).
 *
 * This used to be an 800ms debounce, one `setDoc` of the recipe's whole entry
 * array and a `catch` that logged. A cook note is now kept here precisely so
 * that it outlives the plan (#795); a copy that could vanish on a locked phone
 * or a flaky network would only move the loss somewhere quieter.
 *
 * Routes through the household path when paired; otherwise per-user. See
 * cookLogSyncService for path resolution.
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  addCookLogEntry,
  upsertCookLogEntry,
  removeCookLogEntry,
  markCookLogAcked,
  pendingKey,
  type CookLogPendingOp,
} from './cookLogSlice';
import {
  putCookLogEntryInCloud,
  deleteCookLogEntryInCloud,
} from '../services/cookLogSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { trackEvent } from '../services/analyticsService';
import type { RootState } from './store';

export const cookLogSyncMiddleware = createListenerMiddleware();

type Dispatch = (action: unknown) => unknown;

/** Operations already on the wire, so a retry pass does not send them twice. */
const inFlight = new Set<string>();

/** Test seam. */
export const resetCookLogSyncState = (): void => {
  inFlight.clear();
};

const sendOne = async (
  op: CookLogPendingOp,
  getState: () => unknown,
  dispatch: Dispatch
): Promise<void> => {
  const key = pendingKey(op.recipeId, op.entryId);
  if (inFlight.has(key)) return;

  const state = getState() as RootState;
  const userId = state.auth?.user?.uid;
  if (!userId) return;
  const householdId = state.auth?.householdId ?? null;

  inFlight.add(key);
  try {
    if (op.op === 'put') {
      const entry = state.cookLog.entries[op.recipeId]?.find((e) => e.id === op.entryId);
      if (entry) {
        await putCookLogEntryInCloud(userId, entry, householdId);
      }
      // No entry means it was removed before it went out; the delete that
      // removed it has replaced this op in the outbox, so acking a 'put' is a
      // no-op there and releases a stale one here.
    } else {
      await deleteCookLogEntryInCloud(userId, op.recipeId, op.entryId, householdId);
    }
    dispatch(markCookLogAcked(op));
  } catch (error) {
    // Left in the outbox on purpose: the retry passes will send it again.
    console.error(`Failed to sync cook log ${op.recipeId}/${op.entryId}:`, error);
    trackEvent('cook_log_sync_failed', {
      op: op.op,
      mirrored: op.entryId.startsWith('meal-'),
      scope: householdId ? 'household' : 'personal',
      reason: (error as { code?: string })?.code ?? String(error).slice(0, 120),
    });
  } finally {
    inFlight.delete(key);
  }

  // Changed again while this write was in flight: the newer op is still in the
  // outbox, send it now rather than at the next retry pass.
  const after = (getState() as RootState).cookLog.pending?.[key];
  if (after && after.op !== op.op) {
    await sendOne(after, getState, dispatch);
  }
};

cookLogSyncMiddleware.startListening({
  matcher: isAnyOf(addCookLogEntry, upsertCookLogEntry, removeCookLogEntry),
  effect: async (action, listenerApi) => {
    if (!isFirebaseEnabled()) return;
    const payload = action.payload as { recipeId: string; id?: string; entryId?: string };
    const entryId = payload.entryId ?? payload.id;
    if (!payload.recipeId || !entryId) return;

    const state = listenerApi.getState() as RootState;
    const op = state.cookLog.pending?.[pendingKey(payload.recipeId, entryId)];
    if (!op) return;
    await sendOne(op, listenerApi.getState, listenerApi.dispatch as unknown as Dispatch);
  },
});

/**
 * Send everything the outbox is holding, now. Called on boot, on reconnect and
 * when the tab comes back, beside the ratings outbox.
 */
export const flushPendingCookLogSync = async (
  getState: () => unknown,
  dispatch: Dispatch
): Promise<void> => {
  if (!isFirebaseEnabled()) return;
  const pending = (getState() as RootState).cookLog?.pending ?? {};
  await Promise.allSettled(
    Object.values(pending).map((op) => sendOne(op, getState, dispatch))
  );
};

export default cookLogSyncMiddleware;
